import { type KeyboardEvent, type MouseEvent, useId, useState } from 'react';
import {
  normalizeProjectBadgeChars,
  projectBadgeCharsMaxLength,
  projectBadgeColorOptions,
  resolveProjectBadgeSwatch,
} from '@/shared/data/projectBadges';
import { r, s } from '@/theme/tokens';
import { Box } from '@/ui';
import { Checkbox, FormHelperText, Menu, MenuItem, Text, TextField } from '@/ui/primitives';
import { formFieldLabelSx } from '@/ui/primitives/formFieldStyles';
import type { SxProps, Theme } from '@/ui/primitives/types';
import type { ProjectBadgeColor } from '@flowqa/shared';
import type { ReactElement } from 'react';

type ProjectBadgeFieldProps = {
  enabled: boolean;
  onEnabledChange: (value: boolean) => void;
  chars: string;
  color: ProjectBadgeColor;
  onCharsChange: (value: string) => void;
  onColorChange: (value: ProjectBadgeColor) => void;
  disabled?: boolean;
  textFieldColumnWidth?: string;
  checkboxMarginLeft?: string;
  preview?: ReactElement;
};

const swatchSize = '18px';
const swatchRadius = r('sm');
const controlSize = '32px';
const colorSelectWidth = controlSize;
const checkboxColumnWidth = `calc(${s('xl')} + ${s('md')})`;
const fieldBackgroundColor = 'var(--surface-page)';

const swatchSx = {
  display: 'inline-flex',
  width: swatchSize,
  height: swatchSize,
  minWidth: swatchSize,
  minHeight: swatchSize,
  maxWidth: swatchSize,
  maxHeight: swatchSize,
  flex: '0 0 auto',
  boxSizing: 'border-box',
  borderRadius: swatchRadius,
  border: '1px solid',
  borderColor: 'divider',
} as const;

const projectBadgeHelperTextSx = {
  mt: s('xxs'),
  fontSize: '13px',
} as const;
const projectBadgeLabelSx = [formFieldLabelSx, { fontWeight: 400 }] as SxProps<Theme>;

export const ProjectBadgeField = ({
  enabled,
  onEnabledChange,
  chars,
  color,
  onCharsChange,
  onColorChange,
  disabled = false,
  textFieldColumnWidth = 'minmax(0, 1fr)',
  checkboxMarginLeft = s('none'),
  preview,
}: ProjectBadgeFieldProps): ReactElement => {
  const fieldId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const controlsDisabled = disabled || !enabled;

  const handleOpenColorMenu = (event: MouseEvent<HTMLElement>): void => {
    if (controlsDisabled) return;
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseColorMenu = (): void => {
    setAnchorEl(null);
  };

  const handleColorKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (controlsDisabled) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `${textFieldColumnWidth} ${colorSelectWidth} auto ${checkboxColumnWidth}`,
        alignItems: 'start',
        columnGap: s('sm'),
        rowGap: s('sm'),
        width: 'fit-content',
        maxWidth: '100%',
      }}
    >
      <Text role="body" component="label" htmlFor={fieldId} sx={projectBadgeLabelSx}>
        Badge
      </Text>
      <Box />
      <Box />
      <Box />
      <TextField
        id={fieldId}
        fieldStyle="settings"
        inputHeight={32}
        value={chars}
        placeholder="P"
        slotProps={{ htmlInput: { maxLength: projectBadgeCharsMaxLength } }}
        disabled={controlsDisabled}
        onChange={(event) => {
          onCharsChange(normalizeProjectBadgeChars(event.target.value));
        }}
      />
      <Box
        role="button"
        tabIndex={controlsDisabled ? -1 : 0}
        aria-label="Choose project badge color"
        onClick={handleOpenColorMenu}
        onKeyDown={handleColorKeyDown}
        sx={{
          minWidth: colorSelectWidth,
          width: colorSelectWidth,
          maxWidth: colorSelectWidth,
          minHeight: controlSize,
          height: controlSize,
          maxHeight: controlSize,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: r('sm'),
          backgroundColor: fieldBackgroundColor,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: controlsDisabled ? 'default' : 'pointer',
          opacity: controlsDisabled ? 0.6 : 1,
        }}
      >
        <Box
          component="span"
          sx={[swatchSx, { backgroundColor: resolveProjectBadgeSwatch(color) }]}
        />
      </Box>
      {preview ? (
        <Box sx={{ height: controlSize, display: 'flex', alignItems: 'center', ml: s('xs') }}>
          {preview}
        </Box>
      ) : (
        <Box />
      )}
      <Box
        sx={{
          width: checkboxColumnWidth,
          minWidth: checkboxColumnWidth,
          maxWidth: checkboxColumnWidth,
          height: controlSize,
          minHeight: controlSize,
          maxHeight: controlSize,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          ml: checkboxMarginLeft,
        }}
      >
        <Checkbox
          checked={enabled}
          disabled={disabled}
          onChange={(event) => {
            onEnabledChange(event.target.checked);
          }}
          slotProps={{ input: { 'aria-label': 'Enable project badge' } }}
          sx={{
            p: s('none'),
            width: '24px',
            minWidth: '24px',
            maxWidth: '24px',
            height: '24px',
            minHeight: '24px',
            maxHeight: '24px',
            '& .MuiSvgIcon-root': {
              fontSize: '24px',
            },
          }}
        />
      </Box>
      <FormHelperText sx={projectBadgeHelperTextSx}>Up to 2 characters.</FormHelperText>
      <Box />
      <Box />
      <Box />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseColorMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: colorSelectWidth,
              width: colorSelectWidth,
              maxWidth: colorSelectWidth,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: `${r('sm')} !important`,
              backgroundColor: fieldBackgroundColor,
              backgroundImage: 'none',
              boxShadow: 'none',
              overflow: 'hidden',
              clipPath: `inset(0 round ${r('sm')})`,
            },
          },
          list: {
            sx: {
              p: s('none'),
              m: s('none'),
              backgroundColor: fieldBackgroundColor,
            },
          },
        }}
      >
        {projectBadgeColorOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => {
              onColorChange(option.value);
              handleCloseColorMenu();
            }}
            sx={{
              minHeight: '28px',
              height: '28px',
              px: s('md'),
              py: s('none'),
              justifyContent: 'center',
              borderRadius: r('none'),
              backgroundColor: color === option.value ? 'action.selected' : undefined,
            }}
          >
            <Box component="span" sx={[swatchSx, { backgroundColor: option.swatch }]} />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
